import ArrowDownTrayIcon from '@heroicons/react/24/outline/ArrowDownTrayIcon';
import CheckIcon from '@heroicons/react/24/outline/CheckIcon';
import EyeIcon from '@heroicons/react/24/outline/EyeIcon';
import TagIcon from '@heroicons/react/24/outline/TagIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { PaginationPage } from '@/components/core/PaginationPage/PaginationPage';
import { type ToolbarAction, ToolbarTable, type ToolbarTableLabels } from '@/components/core/ToolbarTable';
import { useFormatAttributeValue } from '@/components/datamodel/utils/formatAttributeValue';
import { FocusAttributeProvider } from '@/dialogs/item-editor/Context/FocusAttributeProvider';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useCursorPaging } from '@/pages/dam/utils/useCursorPaging';
import { holdAngularRoute, navigateTo, replaceLocationSearch } from '@/services/angularjs-bridge';
import { cn } from '@/services/utils';

import {
    ASSET_CARD_LAYOUT,
    type AssetApiRecord,
    type AssetLabel,
    assetLabelTag,
    type AssetsQueryVariables,
    useAssetFilterGroups,
    useAssets,
    useLabels,
} from './api';
import { DAM_BULK_ACTION_LIMIT, type DamBulkFailure, deleteAssets, resolveSelectionKeys, withFilenames } from './api/damBulk';
import {
    createLabel,
    deleteLabel,
    fetchLabelledAssets,
    type LabelledAsset,
    planLabelChanges,
    renameLabel,
    runLabelChanges,
} from './api/damLabels';
import { DAM_CARD_VIEW_PREFERENCE, DAM_LIBRARY_VIEW_PREFERENCE, useDamPreference } from './api/damPreferences';
import { DamAssetCard } from './components/DamAssetCard';
import { DamAssetList } from './components/DamAssetList';
import { DamBulkFailuresDialog } from './components/DamBulkFailuresDialog';
import { DamCardViewMenu } from './components/DamCardViewMenu';
import { DamDeleteAssetsDialog } from './components/DamDeleteAssetsDialog';
import { DamDeleteTagDialog } from './components/DamDeleteTagDialog';
import { type DamFilterChip, DamFilterChips } from './components/DamFilterChips';
import { DamFilterPanel, toggleValue } from './components/DamFilterPanel';
import { DamLibraryEmptyState, type DamLibraryEmptyStateVariant } from './components/DamLibraryEmptyState';
import { DamLibraryHeader } from './components/DamLibraryHeader';
import { DamLibrarySearchRow } from './components/DamLibrarySearchRow';
import { DamLibraryToolbar } from './components/DamLibraryToolbar';
import { DamManageTagsDialog } from './components/DamManageTagsDialog';
import { DAM_SIDE_PANEL_PAGE_PADDING } from './components/DamSidePanel';
import { DamTagsPanel } from './components/DamTagsPanel';
import { DamUploadWizard } from './components/DamUploadWizard';
import { DamAssetDetailPage } from './DamAssetDetailPage';
import { damAssetPath, isDamLibraryOrAssetPath, parseDamAssetId } from './damRoutes';
import {
    applyCardViewSelection,
    cardViewOptionsOf,
    DAM_CARD_VIEW_OPTIONS_LAYOUT,
    defaultCardViewSelection,
    normalizeCardViewSelection,
} from './utils/damCardView';
import { type DamLibrarySort, findDamLibrarySort } from './utils/damLibrarySorts';
import {
    countFilterValues,
    type DamFilterValues,
    type DamLibraryViewMode,
    parseDamLibraryUrlState,
    toDamLibrarySearch,
    toDamLibraryUrlParams,
    toFilterParams,
} from './utils/damLibraryUrlState';
import { useDamFieldLabel } from './utils/useDamAssetContent';
import { useDamAssetsRealtimeUpdates } from './utils/useDamAssetsRealtimeUpdates';
import { useDamSelection } from './utils/useDamSelection';
import type { DamCreateResult } from './utils/useDamUpload';
import { useRefetchOnPageRestore } from './utils/useRefetchOnPageRestore';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;
const TAGS_ATTRIBUTE = 'tags__';
const CHIP_ID_SEPARATOR = '\u0000';

type DamLibraryPanel = 'filter' | 'tags';

interface DamTagDialogState {
    count: number;
    /** Tags of the selected assets; `undefined` while loading. */
    assets?: LabelledAsset[];
    progress?: { done: number; count: number };
}

/** Label id of a `tags__` filter value (`assetLabel:<id>`). */
const labelIdOf = (tag: string) => tag.slice(assetLabelTag('').length);

const keyOf = (asset: AssetApiRecord) => String(asset.primaryKey__);

function DamLibraryPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const { formatValue } = useFormatAttributeValue();
    const [initialState] = useState(() => parseDamLibraryUrlState(window.location.search));
    const [openAssetId, setOpenAssetId] = useState<string | undefined>(undefined);
    const libraryScrollRef = useRef(0);
    const [searchTerm, setSearchTerm] = useState(initialState.keyword);
    const [keyword, setKeyword] = useState(initialState.keyword);
    const [sort, setSort] = useState<DamLibrarySort>(initialState.sort);
    const [filters, setFilters] = useState<DamFilterValues>(initialState.filters);
    const [openPanel, setOpenPanel] = useState<DamLibraryPanel | null>(null);
    const [viewOverride, setViewOverride] = useState<DamLibraryViewMode | undefined>(initialState.view);
    const viewPreference = useDamPreference<{ viewMode: DamLibraryViewMode }>(DAM_LIBRARY_VIEW_PREFERENCE);
    const cardViewPreference = useDamPreference<{ fields: string[]; known?: string[] }>(DAM_CARD_VIEW_PREFERENCE);
    const viewMode: DamLibraryViewMode = viewOverride ?? (viewPreference.value?.viewMode === 'list' ? 'list' : 'grid');
    const fieldLabel = useDamFieldLabel();
    const filterGroups = useAssetFilterGroups();
    const activeTagCount = filters[TAGS_ATTRIBUTE]?.length ?? 0;
    const [tagDialog, setTagDialog] = useState<DamTagDialogState | null>(null);
    const [tagToDelete, setTagToDelete] = useState<{ label: AssetLabel; isDeleting: boolean } | null>(null);
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const { data: labels = [], isLoading: isLoadingLabels, refetch: refetchLabels } = useLabels({
        enabled: openPanel === 'tags' || activeTagCount > 0 || tagDialog !== null,
    });

    const sortDefinition = findDamLibrarySort(sort);
    const variables = useMemo<AssetsQueryVariables>(() => ({
        count: PAGE_SIZE,
        layout: ASSET_CARD_LAYOUT,
        keyword: keyword || undefined,
        filter: toFilterParams(filters),
        sort: sortDefinition?.field ? [sortDefinition.field] : undefined,
        direction: sortDefinition?.direction ? [sortDefinition.direction] : undefined,
    }), [keyword, filters, sortDefinition]);

    const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } = useAssets({ variables });
    useRefetchOnPageRestore(refetch);
    useDamAssetsRealtimeUpdates();

    const { currentPage, goToPage, resetPaging } = useCursorPaging({
        loadedPageCount: data?.pages.length ?? 0,
        hasNextPage: Boolean(hasNextPage),
        fetchNextPage,
    });

    const records = data?.pages[currentPage - 1]?.data ?? [];

    // Like the item list with its editor, the library stays mounted under an open asset, so
    // results, selection and panels survive; the route is held for list and detail URLs.
    useEffect(() => holdAngularRoute(isDamLibraryOrAssetPath, (pathname) => setOpenAssetId(parseDamAssetId(pathname))), []);

    // Layout effects, so the hidden library's shorter page cannot overwrite the position first.
    useLayoutEffect(() => {
        if (openAssetId) {
            return undefined;
        }
        const remember = () => {
            libraryScrollRef.current = window.scrollY;
        };
        window.addEventListener('scroll', remember, { passive: true });
        return () => window.removeEventListener('scroll', remember);
    }, [openAssetId]);
    const shownAssetIdRef = useRef(openAssetId);
    useLayoutEffect(() => {
        if (shownAssetIdRef.current === openAssetId) {
            return;
        }
        shownAssetIdRef.current = openAssetId;
        window.scrollTo(0, openAssetId ? 0 : libraryScrollRef.current);
    }, [openAssetId]);
    const totalCount = data?.pages[0]?.found ?? 0;
    const selection = useDamSelection(totalCount);
    const contentRef = useRef<HTMLDivElement>(null);
    const [deleteProgress, setDeleteProgress] = useState<{ count: number; done?: number } | null>(null);
    const [bulkFailures, setBulkFailures] = useState<{ title: string; failures: DamBulkFailure[] } | null>(null);
    const cardLayout = dataModel?.assetLayout(ASSET_CARD_LAYOUT);
    const cardViewOptions = useMemo(() => cardViewOptionsOf(dataModel?.assetLayout(DAM_CARD_VIEW_OPTIONS_LAYOUT)), [dataModel]);
    const defaultCardView = useMemo(() => defaultCardViewSelection(cardLayout, cardViewOptions), [cardLayout, cardViewOptions]);
    const savedCardViewFields = cardViewPreference.value?.fields;
    const savedCardViewKnown = cardViewPreference.value?.known;
    const cardView = useMemo(
        () => normalizeCardViewSelection(savedCardViewFields, cardViewOptions, defaultCardView, savedCardViewKnown) ?? defaultCardView,
        [savedCardViewFields, savedCardViewKnown, cardViewOptions, defaultCardView],
    );
    const layout = useMemo(
        () => (cardViewOptions.length > 0 ? applyCardViewSelection(cardLayout, cardViewOptions, cardView) : cardLayout),
        [cardLayout, cardViewOptions, cardView],
    );
    const cardViewMenuOptions = cardViewOptions.map((option) => ({
        name: option.name,
        label: fieldLabel({ ...(dataModel?.attribute(option.name) ?? {}), ...option }),
    }));

    useEffect(() => {
        if (openAssetId) {
            return;
        }
        const state = { keyword, sort, page: currentPage, filters, view: viewOverride };
        replaceLocationSearch(toDamLibraryUrlParams(state), toDamLibrarySearch(state));
    }, [keyword, sort, currentPage, filters, viewOverride, openAssetId]);

    const { clear: clearSelection } = selection;
    useEffect(() => {
        clearSelection();
    }, [keyword, filters, sort, clearSelection]);

    useEffect(() => {
        const next = searchTerm.trim();
        if (next === keyword) {
            return undefined;
        }
        const timeout = window.setTimeout(() => {
            setKeyword(next);
            resetPaging();
        }, next === '' ? 0 : SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timeout);
    }, [searchTerm, keyword, resetPaging]);

    const handleSortChange = useCallback((next: DamLibrarySort) => {
        setSort(next);
        resetPaging();
    }, [resetPaging]);

    const openAsset = useCallback((asset: AssetApiRecord) => {
        navigateTo(damAssetPath(String(asset.primaryKey__)));
    }, []);

    const handleViewModeChange = useCallback((next: DamLibraryViewMode) => {
        setViewOverride(next);
        viewPreference.save({ viewMode: next }).catch(() => toast.error(t('DAM.LIBRARY.PREFERENCE_SAVE_FAILED')));
    }, [viewPreference, toast, t]);

    const handleCardViewSave = useCallback((fields: string[]) => {
        cardViewPreference.save({ fields, known: cardViewOptions.map((option) => option.name) })
            .catch(() => toast.error(t('DAM.LIBRARY.PREFERENCE_SAVE_FAILED')));
    }, [cardViewPreference, cardViewOptions, toast, t]);

    const handlePageChange = useCallback((page: number) => {
        if (!isFetchingNextPage) {
            void goToPage(page);
        }
    }, [goToPage, isFetchingNextPage]);

    const updateFilters = useCallback((next: DamFilterValues) => {
        setFilters(next);
        resetPaging();
    }, [resetPaging]);

    const handleNewSearch = useCallback(() => {
        setSearchTerm('');
        setKeyword('');
        updateFilters({});
    }, [updateFilters]);

    const togglePanel = useCallback((panel: DamLibraryPanel) => {
        setOpenPanel((current) => {
            if (current !== panel) {
                clearSelection();
            }
            return current === panel ? null : panel;
        });
    }, [clearSelection]);

    const handleDeleteConfirm = useCallback(async () => {
        setDeleteProgress((current) => current && { ...current, done: 0 });
        try {
            const keys = await resolveSelectionKeys(selection.selection, {
                keyword: variables.keyword,
                filter: variables.filter,
                sort: variables.sort,
                direction: variables.direction,
            });
            setDeleteProgress({ count: keys.length, done: 0 });
            const result = await deleteAssets(keys, (done) => setDeleteProgress({ count: keys.length, done }));
            clearSelection();
            if (result.failed.length === 0) {
                toast.success(t('DAM.BULK.DELETE_DONE', { count: result.succeeded.length.toLocaleString() }));
            } else {
                setBulkFailures({
                    title: t('DAM.BULK.DELETE_PARTIAL', { failed: result.failed.length.toLocaleString(), count: keys.length.toLocaleString() }),
                    failures: await withFilenames(result.failed),
                });
            }
        } catch {
            toast.error(t('DAM.BULK.DELETE_FAILED'));
        } finally {
            setDeleteProgress(null);
            void refetch();
        }
    }, [selection.selection, variables, clearSelection, refetch, toast, t]);

    const openTagDialog = useCallback(async () => {
        const keys = selection.selection.mode === 'ids' ? [...selection.selection.ids] : [];
        setTagDialog({ count: keys.length });
        try {
            const assets = await fetchLabelledAssets(keys);
            setTagDialog((current) => current && { ...current, assets });
        } catch {
            setTagDialog(null);
            toast.error(t('DAM.TAGS.LOAD_FAILED'));
        }
    }, [selection.selection, toast, t]);

    const handleTagDialogConfirm = useCallback(async (added: string[], removed: string[]) => {
        const assets = tagDialog?.assets ?? [];
        const changes = planLabelChanges(assets, added, removed);
        setTagDialog((current) => current && { ...current, progress: { done: 0, count: changes.length } });
        const result = await runLabelChanges(changes, (done) => {
            setTagDialog((current) => current && { ...current, progress: { done, count: changes.length } });
        });
        setTagDialog(null);
        clearSelection();
        if (result.failed.length === 0) {
            toast.success(t('DAM.TAGS.SAVE_DONE', { count: assets.length.toLocaleString() }));
        } else {
            setBulkFailures({
                title: t('DAM.TAGS.SAVE_PARTIAL', { failed: result.failed.length.toLocaleString(), count: assets.length.toLocaleString() }),
                failures: await withFilenames(result.failed),
            });
        }
        void refetch();
        void refetchLabels();
    }, [tagDialog, clearSelection, refetch, refetchLabels, toast, t]);

    const handleUploadCreated = useCallback((result: DamCreateResult) => {
        if (result.duplicates + result.failed + result.linkFailures === 0) {
            toast.success(t('DAM.UPLOAD.CREATED', { count: result.created.toLocaleString() }));
        } else {
            toast.warning(t('DAM.UPLOAD.CREATED_PARTIAL', {
                count: result.created.toLocaleString(),
                failed: (result.duplicates + result.failed).toLocaleString(),
                links: result.linkFailures.toLocaleString(),
            }));
        }
        void refetch();
    }, [refetch, toast, t]);

    const handleCreateTag = useCallback(async (name: string): Promise<AssetLabel | undefined> => {
        try {
            const label = await createLabel(name);
            await refetchLabels();
            toast.success(t('DAM.TAGS.CREATED', { name: label.name }));
            return label;
        } catch {
            toast.error(t('DAM.TAGS.CREATE_FAILED'));
            return undefined;
        }
    }, [refetchLabels, toast, t]);

    const handleRenameTag = useCallback(async (label: AssetLabel, name: string) => {
        try {
            await renameLabel(label.id, name);
            await refetchLabels();
            toast.success(t('DAM.TAGS.RENAME_DONE', { name }));
            return true;
        } catch {
            toast.error(t('DAM.TAGS.RENAME_FAILED'));
            return false;
        }
    }, [refetchLabels, toast, t]);

    const handleDeleteTagConfirm = useCallback(async () => {
        if (!tagToDelete) {
            return;
        }
        const { label } = tagToDelete;
        setTagToDelete({ label, isDeleting: true });
        try {
            await deleteLabel(label.id);
            const tag = assetLabelTag(label.id);
            if (filters[TAGS_ATTRIBUTE]?.includes(tag)) {
                updateFilters(toggleValue(filters, TAGS_ATTRIBUTE, tag));
            }
            toast.success(t('DAM.TAGS.DELETE_DONE', { name: label.name }));
        } catch {
            toast.error(t('DAM.TAGS.DELETE_FAILED'));
        } finally {
            setTagToDelete(null);
            void refetchLabels();
        }
    }, [tagToDelete, filters, updateFilters, refetchLabels, toast, t]);

    /** Bulk actions run per asset for 1 to 100 ticked assets; "Select all" needs the asset selection API (D-039, LAX-10482). */
    const isExplicitSelection = selection.selection.mode === 'ids';
    const canRunBulkAction = isExplicitSelection && selection.selectedCount > 0 && selection.selectedCount <= DAM_BULK_ACTION_LIMIT;
    const bulkActions = useMemo<ToolbarAction[]>(() => [
        {
            id: 'tags',
            label: t('DAM.BULK.MANAGE_TAGS'),
            icon: TagIcon,
            disabled: !canRunBulkAction,
            onClick: () => void openTagDialog(),
        },
        { id: 'currency', label: t('DAM.BULK.CONFIRM_CURRENCY'), icon: CheckIcon, disabled: true },
        { id: 'download', label: t('DAM.BULK.DOWNLOAD'), icon: ArrowDownTrayIcon, disabled: true },
        { id: 'review', label: t('DAM.BULK.REQUEST_REVIEW'), icon: EyeIcon, disabled: true },
        {
            id: 'delete',
            label: t('DAM.BULK.DELETE'),
            icon: TrashIcon,
            variant: 'ghostDestructive',
            disabled: !canRunBulkAction,
            onClick: () => setDeleteProgress({ count: selection.selectedCount }),
        },
    ], [canRunBulkAction, openTagDialog, selection.selectedCount, t]);

    const toolbarLabels = useMemo<ToolbarTableLabels>(() => {
        const counter = (selected: number, total: number) => (
            <>
                <strong>{selected.toLocaleString()}</strong>
                {` ${t('DAM.BULK.OF')} `}
                <strong>{total.toLocaleString()}</strong>
                {` ${t('DAM.BULK.SELECTED')}`}
            </>
        );
        return { close: t('DAM.BULK.CLOSE'), partialSelected: counter, allSelected: (total) => counter(total, total) };
    }, [t]);

    const handleToggleLabel = useCallback((labelId: string) => {
        updateFilters(toggleValue(filters, TAGS_ATTRIBUTE, assetLabelTag(labelId)));
    }, [filters, updateFilters]);

    const selectedLabelIds = useMemo(() => (filters[TAGS_ATTRIBUTE] ?? []).map(labelIdOf), [filters]);

    const chips = useMemo<DamFilterChip[]>(() => Object.entries(filters).flatMap(([attributeName, values]) => {
        if (attributeName === TAGS_ATTRIBUTE) {
            return values.map((value) => ({
                id: `${attributeName}${CHIP_ID_SEPARATOR}${value}`,
                label: labels.find((label) => label.id === labelIdOf(value))?.name ?? labelIdOf(value),
                icon: TagIcon,
            }));
        }
        const attribute = dataModel?.attribute(attributeName) ?? { name: attributeName };
        const options = filterGroups.find((group) => group.attributeName === attributeName)?.options ?? [];
        return values.map((value) => ({
            id: `${attributeName}${CHIP_ID_SEPARATOR}${value}`,
            label: options.find((option) => option.key === value)?.label
                ?? formatValue(attribute as Parameters<typeof formatValue>[0], value),
        }));
    }), [filters, dataModel, filterGroups, formatValue, labels]);

    const handleRemoveChip = useCallback((id: string) => {
        const [attributeName, value] = id.split(CHIP_ID_SEPARATOR);
        const remaining = (filters[attributeName] ?? []).filter((entry) => entry !== value);
        const next = { ...filters };
        if (remaining.length > 0) {
            next[attributeName] = remaining;
        } else {
            delete next[attributeName];
        }
        updateFilters(next);
    }, [filters, updateFilters]);

    const activeFilterCount = countFilterValues(filters) - activeTagCount;
    const hasQuery = Boolean(keyword) || countFilterValues(filters) > 0;

    let emptyVariant: DamLibraryEmptyStateVariant | undefined;
    if (isError) {
        emptyVariant = isAxiosError(error) && error.response?.status === 403 ? 'noAccess' : 'error';
    } else if (!isLoading && totalCount === 0) {
        emptyVariant = hasQuery ? 'noResult' : 'noAssets';
    }

    const showSearchAndToolbar = !emptyVariant || emptyVariant === 'noResult';
    const isPanelOpen = showSearchAndToolbar && openPanel !== null && !openAssetId;

    return (
        <div className={cn('tw-flex tw-min-h-full', isPanelOpen && DAM_SIDE_PANEL_PAGE_PADDING)}>
            {openAssetId && (
                <div className="tw-min-w-0 tw-flex-1">
                    {/* Same providers as the page of its own in main.tsx. */}
                    <FocusAttributeProvider>
                        <DamAssetDetailPage key={openAssetId} assetId={openAssetId} onBack={() => window.history.back()} />
                    </FocusAttributeProvider>
                </div>
            )}
            <div ref={contentRef} className={cn('tw-flex tw-min-w-0 tw-flex-1 tw-flex-col', openAssetId && 'tw-hidden')}>
                {emptyVariant !== 'noAccess' && (
                    <DamLibraryHeader onAddFiles={() => setIsUploadOpen(true)}>
                        {showSearchAndToolbar && (
                            <DamLibrarySearchRow
                                searchTerm={searchTerm}
                                onSearchChange={setSearchTerm}
                                activeFilterCount={activeFilterCount}
                                activeTagCount={activeTagCount}
                                onOpenFilter={() => togglePanel('filter')}
                                onOpenTags={() => togglePanel('tags')}
                            >
                                <DamFilterChips chips={chips} onRemove={handleRemoveChip} onClear={() => updateFilters({})} />
                            </DamLibrarySearchRow>
                        )}
                    </DamLibraryHeader>
                )}
                <main className="tw-flex tw-flex-1 tw-flex-col tw-gap-6 tw-bg-fill-secondary tw-p-4 md:tw-p-8 xl:tw-px-10">
                    {showSearchAndToolbar && (
                        <DamLibraryToolbar
                            totalCount={totalCount}
                            sort={sort}
                            onSortChange={handleSortChange}
                            viewMode={viewMode}
                            onViewModeChange={handleViewModeChange}
                            onSelectAll={selection.selectAll}
                            isAllSelected={selection.selectAllState === 'all'}
                            isSomeSelected={selection.selectAllState === 'some'}
                            cardViewMenu={(
                                <DamCardViewMenu
                                    options={cardViewMenuOptions}
                                    selection={cardView}
                                    defaultSelection={defaultCardView}
                                    onSave={handleCardViewSave}
                                />
                            )}
                        />
                    )}
                    {isLoading && (
                        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center">
                            <Loader />
                        </div>
                    )}
                    {emptyVariant && (
                        <DamLibraryEmptyState
                            variant={emptyVariant}
                            onAddFiles={() => setIsUploadOpen(true)}
                            onNewSearch={handleNewSearch}
                            onRetry={() => void refetch()}
                        />
                    )}
                    {!isLoading && !emptyVariant && (
                        <>
                            {viewMode === 'list' ? (
                                <DamAssetList
                                    records={records}
                                    layout={layout}
                                    onEdit={openAsset}
                                    isSelected={(asset) => selection.isSelected(keyOf(asset))}
                                    onSelectedChange={(asset, selected) => selection.setSelected([keyOf(asset)], selected)}
                                    onSelectPage={(selected) => selection.setSelected(records.map(keyOf), selected)}
                                />
                            ) : (
                                <ul
                                    className={cn(
                                        'tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-6 tw-p-0 md:tw-grid-cols-2 lg:tw-grid-cols-3',
                                        isPanelOpen ? 'xl:tw-grid-cols-2 2xl:tw-grid-cols-3' : 'xl:tw-grid-cols-4',
                                    )}
                                >
                                    {records.map((asset) => (
                                        <li key={String(asset.primaryKey__)} className="tw-min-w-0">
                                            <DamAssetCard
                                                asset={asset}
                                                layout={layout}
                                                isSelected={selection.isSelected(keyOf(asset))}
                                                onSelectedChange={(selected) => selection.setSelected([keyOf(asset)], selected)}
                                                onEdit={() => openAsset(asset)}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <PaginationPage
                                totalItems={totalCount}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                navigation="compact"
                                itemsPerPage={PAGE_SIZE}
                            />
                        </>
                    )}
                </main>
                {!openAssetId && (
                    <ToolbarTable
                        selectedCount={selection.selectedCount}
                        totalCount={totalCount}
                        actions={bulkActions}
                        labels={toolbarLabels}
                        allSelected={selection.selectAllState === 'all'}
                        onClose={clearSelection}
                        boundsRef={contentRef}
                    />
                )}
                {bulkFailures && (
                    <DamBulkFailuresDialog
                        title={bulkFailures.title}
                        failures={bulkFailures.failures}
                        onSelectFailed={(primaryKeys) => {
                            clearSelection();
                            selection.setSelected(primaryKeys, true);
                            setBulkFailures(null);
                        }}
                        onClose={() => setBulkFailures(null)}
                    />
                )}
                {deleteProgress && (
                    <DamDeleteAssetsDialog
                        count={deleteProgress.count}
                        progress={deleteProgress.done}
                        onConfirm={() => void handleDeleteConfirm()}
                        onClose={() => setDeleteProgress(null)}
                    />
                )}
                {tagDialog && (
                    <DamManageTagsDialog
                        count={tagDialog.count}
                        assets={tagDialog.assets}
                        labels={labels}
                        onCreateLabel={handleCreateTag}
                        onConfirm={(added, removed) => void handleTagDialogConfirm(added, removed)}
                        progress={tagDialog.progress}
                        onClose={() => setTagDialog(null)}
                    />
                )}
                {isUploadOpen && (
                    <DamUploadWizard
                        onClose={() => setIsUploadOpen(false)}
                        onCreated={handleUploadCreated}
                    />
                )}
                {tagToDelete && (
                    <DamDeleteTagDialog
                        label={tagToDelete.label}
                        isDeleting={tagToDelete.isDeleting}
                        onConfirm={() => void handleDeleteTagConfirm()}
                        onClose={() => setTagToDelete(null)}
                    />
                )}
            </div>
            {isPanelOpen && openPanel === 'filter' && (
                <DamFilterPanel groups={filterGroups} filters={filters} onChange={updateFilters} onClose={() => setOpenPanel(null)} />
            )}
            {isPanelOpen && openPanel === 'tags' && (
                <DamTagsPanel
                    labels={labels}
                    isLoading={isLoadingLabels}
                    selectedLabelIds={selectedLabelIds}
                    onToggleLabel={handleToggleLabel}
                    onClose={() => setOpenPanel(null)}
                    onCreateTag={async (name) => Boolean(await handleCreateTag(name))}
                    onRenameTag={handleRenameTag}
                    onDeleteTag={(label) => setTagToDelete({ label, isDeleting: false })}
                />
            )}
        </div>
    );
}

export { DamLibraryPage };
